import { Box, Typography, Button, Modal, TextField, useMediaQuery } from "@mui/material";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { PersonAdd, Edit, PersonRemove, PeopleAlt, Group, Lock } from "@mui/icons-material";
import UserImage from "components/UserImage";
import { useDispatch } from "react-redux";
import { setUser } from "state";
import { Snackbar, Alert, Tooltip, IconButton, Backdrop} from "@mui/material";
import { FaUserClock } from "react-icons/fa";
import { CheckCircle } from "lucide-react";
import CloseIcon from '@mui/icons-material/Close';

import Cropper from "react-easy-crop";
import { getCroppedImg } from "./utils/cropImage";
import { Edit as EditIcon } from "@mui/icons-material";
import { toast } from "react-toastify";
// Add these imports at the top of ProfilePage.jsx
import { CircularProgress } from "@mui/material";
import { VideoLibrary, VideocamOff, PlayArrow } from '@mui/icons-material';
import { AiOutlinePlayCircle, AiOutlineHeart, AiOutlineShareAlt } from 'react-icons/ai';
import { FaRegComment } from 'react-icons/fa';




// Import statements at the top
import { Client, Storage, ID, Query, Databases} from "appwrite";

const ProfilePage = () => {
  const [user, setUser] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({ firstName: "", lastName: "", username: "", bio: "" });

  const { userId } = useParams();
  const token = useSelector((state) => state.token);
  const loggedInUser = useSelector((state) => state.user);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const { _id, picturePath, firstName, lastName, bio, followers, friends } = useSelector((state) => state.user);

  const [isCropping, setIsCropping] = useState(false);
  const [imageSrc, setImageSrc] = useState(null);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [isFollowing, setIsFollowing] = useState(false);

  const [showFollowersModal, setShowFollowersModal] = useState(false);
  const [showFollowingModal, setShowFollowingModal] = useState(false);
  const [followersList, setFollowersList] = useState([]);
  const [followingList, setFollowingList] = useState([]);
  const [isPendingRequest, setIsPendingRequest] = useState(false);
  // Add this near your other state variables
const isDesktop = useMediaQuery('(min-width:1024px)');
// Add these state variables near the other states in ProfilePage.jsx
const [userVideos, setUserVideos] = useState([]);
const [isLoadingVideos, setIsLoadingVideos] = useState(false);
// Inside your component, add this state
const [isUploading, setIsUploading] = useState(false);
const [uploadError, setUploadError] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 }); // Add this line
  const [zoom, setZoom] = useState(1); // Add this line
  
  
  // Media queries for responsive design
  const isNonMobileScreens = useMediaQuery("(min-width:1000px)");
  const isTabletScreens = useMediaQuery("(min-width:600px) and (max-width:999px)");
  const isMobileScreens = useMediaQuery("(max-width:599px)");

  // Add these constants to your ProfilePage component
const DATABASE_ID = "681f7caf0033c79e0bc0";
const FOLLOWERS_COLLECTION_ID = "681f7e8e0028ee01df31";
const FOLLOW_REQUESTS_COLLECTION_ID = "681f7d25000d9b5e446c";
const BUCKET_ID = process.env.REACT_APP_APPWRITE_BUCKET_ID;
const COLLECTION_ID = "681f80c000210a12ef65";


  // Initialize Appwrite client outside of the component
const appwriteClient = new Client();
appwriteClient
  .setEndpoint(process.env.REACT_APP_APPWRITE_ENDPOINT || "https://cloud.appwrite.io/v1")
  .setProject(process.env.REACT_APP_APPWRITE_PROJECT_ID);

  const databases = new Databases(appwriteClient);

const storage = new Storage(appwriteClient);

// This function handles the "Save" button click in the image cropper

// Add this function to handle image file selection
const handleImageChange = (event) => {
  if (event.target.files && event.target.files.length > 0) {
    const file = event.target.files[0];
    
    // Check file size (limit to 5MB)
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      setUploadError("Image size must be less than 5MB");
      return;
    }
    
    // Create file URL for cropper
    const reader = new FileReader();
    reader.onload = () => {
      setImageSrc(reader.result);
      setIsCropping(true); // Open cropping modal
      setUploadError(null); // Reset any previous errors
    };
    reader.readAsDataURL(file);
  }
};

// Update the handleCropComplete function to properly use the cropped image
const handleCropComplete = async () => {
  if (!imageSrc || !croppedAreaPixels) {
    setUploadError("Invalid image or crop selection");
    return;
  }

  try {
    setIsUploading(true);
    
    // Get the cropped image as a File object with the unified cropImage.js
    // Note: Make sure your getCroppedImg function returns a File object
    const croppedImage = await getCroppedImg(imageSrc, croppedAreaPixels, "file");
    
    // Upload the cropped image using your existing function
    await handleProfileImageUpload(croppedImage);
  } catch (error) {
    // console.error("Error processing image:", error);
    setUploadError(error.message || "Failed to process image");
    setIsUploading(false);
  }
};

// Add these utility functions to handle errors
const handleImageUploadError = (error) => {
  // console.error("Image upload error:", error);
  
  // Determine the appropriate error message
  let errorMessage = "Failed to upload profile picture";
  
  if (error.message) {
    errorMessage = error.message;
  }
  
  // Handle specific Appwrite error codes
  if (error.code) {
    switch (error.code) {
      case 401:
        errorMessage = "Unauthorized. Please log in again";
        break;
      case 413:
        errorMessage = "Image file is too large";
        break;
      case 415:
        errorMessage = "Unsupported image format";
        break;
      default:
        if (error.response) {
          errorMessage = `Server error: ${error.response}`;
        }
    }
  }
  
  // Set the error state to display to user
  setUploadError(errorMessage);
  
  // You can also use toast notifications if available in your project
  // toast.error(errorMessage);
  
  return errorMessage;
};

// Add this function in ProfilePage.jsx
const fetchUserVideos = async () => {
  if (!userId || !isFollowing) return;
  
  setIsLoadingVideos(true);
  try {
    const response = await databases.listDocuments(
      DATABASE_ID,
      COLLECTION_ID,
      [
        Query.equal('userId', userId),
        Query.orderDesc('createdAt')
      ]
    );

    // Transform Appwrite data to match your post structure
    const videos = response.documents.map(doc => {
      // Generate the video file URL
      const videoUrl = storage.getFileView(
        BUCKET_ID,
        doc.fileId
      );

      return {
        id: doc.$id,
        text: doc.description,
        video_url: videoUrl,
        talent: doc.talent,
        created_at: new Date(doc.createdAt).toLocaleDateString(),
        hashtags: doc.hashtags || [],
        likes: doc.likes || [],
        profile: {
          user_id: doc.userId,
          name: doc.userName || "User",
          image: doc.userImage ? storage.getFileView(BUCKET_ID, doc.userImage) : "/assets/default-profile.jpg"
        }
      };
    });

    setUserVideos(videos);
  } catch (error) {
    // console.error("Error fetching user videos:", error);
  } finally {
    setIsLoadingVideos(false);
  }
};

// Replace the existing function that handles the image cropping and upload
// Function to handle profile image upload
// Modified handleProfileImageUpload to include better error handling
const handleProfileImageUpload = async (croppedImage) => {
  setIsUploading(true);
  setUploadError(null);

  try {
    // Validate the image file
    if (!croppedImage || !(croppedImage instanceof File)) {
      throw new Error("Invalid image file");
    }
    
    // Check file size (limit to 5MB)
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (croppedImage.size > maxSize) {
      throw new Error("Image size must be less than 5MB");
    }

    // Upload file to Appwrite storage
    let uploadResult;
    try {
      uploadResult = await storage.createFile(
        BUCKET_ID,
        ID.unique(),
        croppedImage
      );
    } catch (uploadError) {
      // console.error("Storage upload error:", uploadError);
      throw new Error(uploadError.message || "Failed to upload to storage service");
    }

    if (!uploadResult || !uploadResult.$id) {
      throw new Error("Failed to get upload result from storage");
    }

    // Update user profile with new picture ID
    const endpoint = `https://my-pers-thing-server.onrender.com/users/${userId}/update-picture`;
    let response;
    
    try {
      response = await fetch(endpoint, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ picturePath: uploadResult.$id })
      });
    } catch (networkError) {
      throw new Error("Network error when updating profile");
    }

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `Server error: ${response.status}`);
    }

    const updatedUser = await response.json();

    // Update local state
    setUser(updatedUser);

    // Update Redux state if it's the current user
    if (_id === userId && updatedUser) {
      dispatch({
        type: "SET_USER",
        payload: {
          ...loggedInUser,
          picturePath: uploadResult.$id
        }
      });
    }

    // Close the cropping modal and show success notification
    setIsCropping(false);
    setOpenSnackbar(true);

  } catch (error) {
    handleImageUploadError(error);
  } finally {
    setIsUploading(false);
  }
};
  // Effect to fetch user data when userId changes
  useEffect(() => {
    if (userId) {
      getUser();
    }
  }, [userId]);

  // Fetch the user data based on URL parameter
  const getUser = async () => {
    try {
      const response = await fetch(`https://my-pers-thing-server.onrender.com/users/${userId}`, {
        method: "GET",
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) {
        throw new Error("Failed to fetch user data");
      }

      const data = await response.json();
      setUser(data);
      setFormData({ 
        firstName: data.firstName, 
        lastName: data.lastName, 
        username: data.username || "", 
        bio: data.bio || "" 
      });
    } catch (error) {
      // console.error("Error fetching user:", error);
    }
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // Handle Save button
  const handleSave = async () => {
    try {
      const response = await fetch(`https://my-pers-thing-server.onrender.com/users/${_id}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        throw new Error("Failed to update profile");
      }

      const updatedUser = await response.json();
      
      setUser(updatedUser.user);
      
      if (_id === userId) {
        dispatch(setUser(updatedUser.user));
      }

      setIsEditing(false);
      setOpenSnackbar(true);
    } catch (error) {
      // console.error("Error updating profile:", error);
      setIsEditing(false);
      setOpenSnackbar(true);
    }
  };  

  // Check if profile belongs to logged-in user
  const isOwnProfile = _id === userId;

  // Check follow status

// Replace the useEffect for checking follow status with this expanded version
useEffect(() => {
  const checkFollowStatus = async () => {
    if (userId && _id && userId !== _id) {
      try {
        // First check if there's an approved follow relationship
        const followResponse = await databases.listDocuments(
          DATABASE_ID,
          FOLLOWERS_COLLECTION_ID,
          [
            Query.equal("followerId", _id),
            Query.equal("followedId", userId)
          ]
        );
        
        // If documents exist, the user is following
        const isCurrentlyFollowing = followResponse.documents.length > 0;
        setIsFollowing(isCurrentlyFollowing);
        
        // If not following, check for pending requests
        if (!isCurrentlyFollowing) {
          const pendingResponse = await databases.listDocuments(
            DATABASE_ID,
            FOLLOW_REQUESTS_COLLECTION_ID,
            [
              Query.equal("requesterId", _id),
              Query.equal("userId", userId),
              Query.equal("status", "pending")
            ]
          );
          
          setIsPendingRequest(pendingResponse.documents.length > 0);
        } else {
          setIsPendingRequest(false);
        }
      } catch (error) {
        // console.error("Error checking follow status:", error);
      }
    }
  };
  
  checkFollowStatus();
}, [userId, _id, databases]);

// Add this function to send follow requests
const sendFollowRequest = async (requesterId, userId) => {
  try {
    // Check for existing requests first
    const existingRequest = await databases.listDocuments(
      DATABASE_ID,
      FOLLOW_REQUESTS_COLLECTION_ID,
      [
        Query.equal("requesterId", requesterId),
        Query.equal("userId", userId)
      ]
    );
    
    // If there's an existing request
    if (existingRequest.documents.length > 0) {
      const existingDoc = existingRequest.documents[0];
      
      // If it's already pending, do nothing
      if (existingDoc.status === "pending") {
        return { isPending: true };
      } 
      
      // Otherwise delete the request (user might be toggling it off)
      await databases.deleteDocument(
        DATABASE_ID,
        FOLLOW_REQUESTS_COLLECTION_ID,
        existingDoc.$id
      );
      
      return { isPending: false, isFollowing: false };
    }
    
    // Create new follow request
    await databases.createDocument(
      DATABASE_ID,
      FOLLOW_REQUESTS_COLLECTION_ID,
      ID.unique(),
      {
        requesterId: requesterId,
        userId: userId,
        status: "pending",
        timestamp: new Date().toISOString()
      }
    );
    
    return { isPending: true };
  } catch (error) {
    // console.error("Error sending follow request:", error);
    throw error;
  }
};
  // Handle follow/unfollow

// Replace the handleFollowToggle function with this updated version:
// Replace handleFollowToggle with this version that uses the request system
const handleFollowToggle = async () => {
  if (!_id || _id === userId) return;
  
  try {
    if (isFollowing) {
      // Find the follow relationship document
      const followDoc = await databases.listDocuments(
        DATABASE_ID,
        FOLLOWERS_COLLECTION_ID,
        [
          Query.equal("followerId", _id),
          Query.equal("followedId", userId)
        ]
      );
      
      if (followDoc.documents.length > 0) {
        // Delete the follow relationship
        await databases.deleteDocument(
          DATABASE_ID,
          FOLLOWERS_COLLECTION_ID,
          followDoc.documents[0].$id
        );
      }
      
      setIsFollowing(false);
      setIsPendingRequest(false);
    } else if (isPendingRequest) {
      // Cancel the pending request
      const pendingDoc = await databases.listDocuments(
        DATABASE_ID,
        FOLLOW_REQUESTS_COLLECTION_ID,
        [
          Query.equal("requesterId", _id),
          Query.equal("userId", userId),
          Query.equal("status", "pending")
        ]
      );
      
      if (pendingDoc.documents.length > 0) {
        await databases.deleteDocument(
          DATABASE_ID,
          FOLLOW_REQUESTS_COLLECTION_ID,
          pendingDoc.documents[0].$id
        );
      }
      
      setIsPendingRequest(false);
    } else {
      // Send a new follow request
      const result = await sendFollowRequest(_id, userId);
      setIsPendingRequest(result.isPending);
      
      // Create notification for follow request
      try {
        await fetch(`https://my-pers-thing-server.onrender.com/notifications`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({
            recipientId: userId,
            senderId: _id,
            type: "follow_request",
            message: `${firstName} ${lastName} wants to follow you`
          })
        });
      } catch (notifError) {
        // console.error("Error creating notification:", notifError);
      }
    }
    
  } catch (error) {
    // console.error("Error updating follow status:", error);
  }
};
  // Fetch followers list
 // Update fetchFollowers function to use Appwrite
const fetchFollowers = async () => {
  try {
    // Fetch follower relationships from Appwrite
    const followerRelations = await databases.listDocuments(
      DATABASE_ID,
      FOLLOWERS_COLLECTION_ID,
      [Query.equal("followedId", userId)]
    );
    
    if (followerRelations.documents.length === 0) {
      setFollowersList([]);
      return;
    }
    
    // Extract follower IDs
    const followerIds = followerRelations.documents.map(doc => doc.followerId);
    
    // Fetch user details for each follower from your API
    const followersData = await Promise.all(
      followerIds.map(async (followerId) => {
        const response = await fetch(`https://my-pers-thing-server.onrender.com/users/${followerId}`, {
          method: "GET",
          headers: { Authorization: `Bearer ${token}` },
        });
        
        if (!response.ok) throw new Error(`Failed to fetch follower ${followerId}`);
        return response.json();
      })
    );
    
    setFollowersList(followersData);
  } catch (error) {
    // console.error("Error fetching followers:", error);
    setFollowersList([]);
  }
};

// Add these functions to implement follow/unfollow in your modals

// Function to check if the logged-in user is following a specific user
const isFollowingUser = async (targetUserId) => {
  try {
    const response = await databases.listDocuments(
      DATABASE_ID,
      FOLLOWERS_COLLECTION_ID,
      [
        Query.equal("followerId", _id),
        Query.equal("followedId", targetUserId)
      ]
    );
    
    return response.documents.length > 0;
  } catch (error) {
    // console.error("Error checking follow status:", error);
    return false;
  }
};

// Function to follow/unfollow user from the modals
const handleFollowToggleInModal = async (targetUserId, isCurrentlyFollowing) => {
  if (!_id || _id === targetUserId) return;
  
  try {
    if (isCurrentlyFollowing) {
      // Find the follow relationship document
      const followDoc = await databases.listDocuments(
        DATABASE_ID,
        FOLLOWERS_COLLECTION_ID,
        [
          Query.equal("followerId", _id),
          Query.equal("followedId", targetUserId)
        ]
      );
      
      if (followDoc.documents.length > 0) {
        // Delete the follow relationship
        await databases.deleteDocument(
          DATABASE_ID,
          FOLLOWERS_COLLECTION_ID,
          followDoc.documents[0].$id
        );
      }
    } else {
      // Create new follow relationship
      await databases.createDocument(
        DATABASE_ID,
        FOLLOWERS_COLLECTION_ID,
        ID.unique(),
        {
          followerId: _id,
          followedId: targetUserId,
          timestamp: new Date().toISOString()
        }
      );
      
      // Optional: Create follow notification
      // Add your notification creation code here
    }
    
    // Refresh the followers and following lists
    fetchFollowers();
    fetchFollowing();
    
    // Update follow counts in the UI
    const updatedFollowersResponse = await databases.listDocuments(
      DATABASE_ID,
      FOLLOWERS_COLLECTION_ID,
      [Query.equal("followedId", userId)]
    );
    
    const updatedFollowingResponse = await databases.listDocuments(
      DATABASE_ID,
      FOLLOWERS_COLLECTION_ID,
      [Query.equal("followerId", userId)]
    );
    
    setUser(prev => ({
      ...prev,
      followers: updatedFollowersResponse.documents || [],
      friends: updatedFollowingResponse.documents || []
    }));
    
  } catch (error) {
    // console.error("Error updating follow status:", error);
  }
};

  // Fetch following list
// Update fetchFollowing function to use Appwrite
const fetchFollowing = async () => {
  try {
    // Fetch following relationships from Appwrite
    const followingRelations = await databases.listDocuments(
      DATABASE_ID,
      FOLLOWERS_COLLECTION_ID,
      [Query.equal("followerId", userId)]
    );
    
    if (followingRelations.documents.length === 0) {
      setFollowingList([]);
      return;
    }
    
    // Extract following IDs
    const followingIds = followingRelations.documents.map(doc => doc.followedId);
    
    // Fetch user details for each following from your API
    const followingData = await Promise.all(
      followingIds.map(async (followingId) => {
        const response = await fetch(`https://my-pers-thing-server.onrender.com/users/${followingId}`, {
          method: "GET",
          headers: { Authorization: `Bearer ${token}` },
        });
        
        if (!response.ok) throw new Error(`Failed to fetch following ${followingId}`);
        return response.json();
      })
    );
    
    setFollowingList(followingData);
  } catch (error) {
    // console.error("Error fetching following:", error);
    setFollowingList([]);
  }
};

      // Replace your useEffect for fetching user data with this updated version
useEffect(() => {
  const fetchUserData = async () => {
    try {
      // Fetch user details from your API
      const response = await fetch(`https://my-pers-thing-server.onrender.com/users/${userId}`, {
        method: "GET",
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if (!response.ok) throw new Error("Failed to fetch user data");
      const userData = await response.json();
      
      // Now fetch the follower count from Appwrite
      const followerCountResponse = await databases.listDocuments(
        DATABASE_ID,
        FOLLOWERS_COLLECTION_ID,
        [Query.equal("followedId", userId)]
      );
      
      // Fetch following count from Appwrite
      const followingCountResponse = await databases.listDocuments(
        DATABASE_ID,
        FOLLOWERS_COLLECTION_ID,
        [Query.equal("followerId", userId)]
      );
      
      // Update the user object with accurate counts from Appwrite
      setUser({
        ...userData,
        followers: followerCountResponse.documents || [],
        friends: followingCountResponse.documents || [] // In your system, "friends" means "following"
      });
      
    } catch (error) {
      // console.error("Error fetching user data:", error);
    }
  };
  
  if (userId) {
    fetchUserData();
  }
}, [userId, token]);

// Add this useEffect after your other useEffects
useEffect(() => {
  if (isFollowing && userId) {
    fetchUserVideos();
  }
}, [isFollowing, userId]);



return (
  <Box
    sx={{
      width: "100%",
      maxWidth: "900px",
      margin: "auto",
      padding: isMobileScreens ? "20px" : isTabletScreens ? "30px" : "40px",
      bgcolor: "white",
      borderRadius: "20px",
      textAlign: "center",
      color: "black",
      fontFamily: "Poppins, sans-serif",
      position: "relative",
      boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
      transition: "all 0.3s ease"
    }}
  >
    {/* "Edit Profile" Button - Only shown on own profile */}
    {isOwnProfile && (
      <Button
        variant="contained"
        startIcon={<Edit />}
        onClick={() => setIsEditing(true)}
        sx={{
          position: "absolute",
          top: "15px",
          right: "20px",
          backgroundColor: "black",
          color: "white",
          borderRadius: "10px",
          textTransform: "none",
          fontSize: isMobileScreens ? "12px" : "14px",
          padding: "6px 15px",
          boxShadow: "none",
          "&:hover": { backgroundColor: "#333" },
          display: isMobileScreens ? "none" : "flex" // Hide on mobile
        }}
      >
        Edit Profile
      </Button>
    )}

    {/* Profile Section */}
    <Box sx={{ position: "relative", display: "inline-block" }}>
  {/* User Profile Image */}
  <UserImage 
    image={user?.picturePath} 
    size={isMobileScreens ? "100px" : isTabletScreens ? "120px" : "150px"}
  />
  
  {/* Edit Profile Image Button - Only shown on own profile */}
  {isOwnProfile && (
    <>
      <input
        type="file"
        accept="image/*"
        style={{ display: "none" }}
        id="profile-pic-upload"
        onChange={(e) => {
          const file = e.target.files[0];
          if (file) {
            // Read the selected file as a data URL
            const reader = new FileReader();
            reader.onload = () => {
              setImageSrc(reader.result);
              setIsCropping(true);
            };
            reader.readAsDataURL(file);
            
            // Reset the input value to allow selecting the same file again
            e.target.value = "";
          }
        }}
      />
      <label htmlFor="profile-pic-upload">
        <IconButton
          component="span"
          sx={{
            position: "absolute",
            bottom: 0,
            right: 0,
            bgcolor: "white",
            borderRadius: "50%",
            padding: "5px",
            cursor: "pointer",
            boxShadow: "0 0 6px rgba(0,0,0,0.2)",
            transition: "transform 0.2s ease",
            "&:hover": {
              transform: "scale(1.1)"
            }
          }}
        >
          <Edit sx={{ fontSize: "18px", color: "#555" }} />
        </IconButton>
      </label>
    </>
  )}
</Box>

    {/* User Information */}
    <Typography 
      variant={isMobileScreens ? "h4" : "h3"} 
      fontWeight="bold" 
      mt={2}
      sx={{ wordBreak: "break-word" }}
    >
      {user?.firstName} {user?.lastName}
    </Typography>

    {/* Username Section */}
    <Typography 
      color="#777" 
      fontSize={isMobileScreens ? "14px" : "16px"} 
      fontWeight="500" 
      mb={2}
      sx={{ wordBreak: "break-word" }}
    >
      {user?.username}
    </Typography>

    <Typography 
      color="#555" 
      mb={3} 
      fontSize={isMobileScreens ? "16px" : "18px"}
      sx={{ 
        padding: isMobileScreens ? "0 10px" : "0 30px",
        wordBreak: "break-word"
      }}
    >
      {user?.bio || "No bio available"}
    </Typography>

    {/* Enhanced Followers/Following Section with better visual cues */}
    <Box 
      sx={{ 
        display: "flex", 
        justifyContent: "center", 
        gap: isMobileScreens ? "15px" : "30px", 
        marginBottom: "20px",
        flexWrap: isMobileScreens ? "wrap" : "nowrap"
      }}
    >
      {/* Followers Card */}
      <Box
        onClick={() => {
          fetchFollowers();
          setShowFollowersModal(true);
        }}
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          padding: "12px 20px",
          cursor: "pointer",
          borderRadius: "12px",
          backgroundColor: "#f8f8f8",
          transition: "all 0.2s ease",
          border: "1px solid #eaeaea",
          "&:hover": {
            backgroundColor: "#f0f0f0",
            transform: "translateY(-3px)",
            boxShadow: "0 4px 10px rgba(0,0,0,0.1)"
          }
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", mb: 0.5 }}>
          <PeopleAlt sx={{ fontSize: 20, mr: 1, color: "#555" }} />
          <Typography 
            variant="h6" 
            fontWeight="bold"
            fontSize={isMobileScreens ? "20px" : "24px"}
          >
            {user?.followers?.length || 0}
          </Typography>
        </Box>
        <Typography 
          variant="body2" 
          color="#777"
          sx={{ fontSize: isMobileScreens ? "14px" : "16px" }}
        >
          Followers
        </Typography>
        <Typography 
          variant="caption" 
          sx={{ 
            color: "#999", 
            fontSize: "12px",
            mt: 0.5,
            display: "flex",
            alignItems: "center"
          }}
        >
          Click to view
        </Typography>
      </Box>

      {/* Following Card */}
      <Box
        onClick={() => {
          fetchFollowing();
          setShowFollowingModal(true);
        }}
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          padding: "12px 20px",
          cursor: "pointer",
          borderRadius: "12px",
          backgroundColor: "#f8f8f8",
          transition: "all 0.2s ease",
          border: "1px solid #eaeaea",
          "&:hover": {
            backgroundColor: "#f0f0f0",
            transform: "translateY(-3px)",
            boxShadow: "0 4px 10px rgba(0,0,0,0.1)"
          }
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", mb: 0.5 }}>
          <Group sx={{ fontSize: 20, mr: 1, color: "#555" }} />
          <Typography 
            variant="h6" 
            fontWeight="bold"
            fontSize={isMobileScreens ? "20px" : "24px"}
          >
            {user?.friends?.length || 0}
          </Typography>
        </Box>
        <Typography 
          variant="body2" 
          color="#777"
          sx={{ fontSize: isMobileScreens ? "14px" : "16px" }}
        >
          Following
        </Typography>
        <Typography 
          variant="caption" 
          sx={{ 
            color: "#999", 
            fontSize: "12px",
            mt: 0.5,
            display: "flex",
            alignItems: "center"
          }}
        >
          Click to view
        </Typography>
      </Box>
    </Box>

    {/* Followers Modal with improved styling */}
    <Modal 
      open={showFollowersModal} 
      onClose={() => setShowFollowersModal(false)}
      aria-labelledby="followers-modal-title"
      // Add better mobile handling for modals
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{
        timeout: 500,
      }}
    >
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          bgcolor: "white",
          boxShadow: 24,
          borderRadius: "16px",
          padding: isMobileScreens ? "20px" : "30px",
          width: isMobileScreens ? "90%" : "450px",
          maxHeight: "70vh",
          overflow: "auto",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <PeopleAlt sx={{ mr: 1.5, color: "#555", fontSize: 24 }} />
            <Typography variant="h5" fontWeight="bold" id="followers-modal-title">
              Followers ({followersList.length})
            </Typography>
          </Box>
          <IconButton 
            onClick={() => setShowFollowersModal(false)}
            sx={{ color: "#555" }}
            aria-label="close followers dialog"
          >
            <CloseIcon />
          </IconButton>
        </Box>
        
        {followersList.length === 0 ? (
          <Box sx={{ 
            textAlign: "center", 
            py: 4,
            color: "#777",
            bgcolor: "#f9f9f9",
            borderRadius: "10px"
          }}>
            <Typography>No followers yet</Typography>
          </Box>
        ) : (
          followersList.map((follower) => (
            <Box 
              key={follower._id}
              sx={{ 
                display: "flex", 
                alignItems: "center", 
                mb: 2, 
                cursor: "pointer", 
                p: 1.5,
                borderRadius: "12px",
                transition: "all 0.2s ease",
                "&:hover": { 
                  backgroundColor: "#f5f5f5",
                  transform: "translateX(5px)" 
                },
              }}
              onClick={() => {
                setShowFollowersModal(false);
                // Use window.open for new tab navigation
                if (isDesktop) {
                  window.open(`/profile/${follower._id}`, '_blank');
                } else {
                  navigate(`/profile/${follower._id}`);
                }
              }}
            >
              <UserImage image={follower.picturePath} size="50px" />
              <Box sx={{ ml: 2, overflow: "hidden", flex: 1 }}>
                <Typography fontWeight="bold" sx={{ wordBreak: "break-word" }}>
                  {follower.firstName} {follower.lastName}
                </Typography>
                <Typography color="text.secondary" fontSize="14px" sx={{ wordBreak: "break-word" }}>
                  {follower.username}
                </Typography>
              </Box>
              <IconButton 
                size="small" 
                sx={{ color: "#555" }}
                onClick={(e) => {
                  e.stopPropagation();
                  // Handle follow/unfollow action
                }}
              >
                <PersonAdd fontSize="small" />
              </IconButton>
            </Box>
          ))
        )}
      </Box>
    </Modal>

    {/* Following Modal with improved styling */}
    <Modal 
      open={showFollowingModal} 
      onClose={() => setShowFollowingModal(false)}
      aria-labelledby="following-modal-title"
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{
        timeout: 500,
      }}
    >
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          bgcolor: "white",
          boxShadow: 24,
          borderRadius: "16px",
          padding: isMobileScreens ? "20px" : "30px",
          width: isMobileScreens ? "90%" : "450px",
          maxHeight: "70vh",
          overflow: "auto",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Group sx={{ mr: 1.5, color: "#555", fontSize: 24 }} />
            <Typography variant="h5" fontWeight="bold" id="following-modal-title">
              Following ({followingList.length})
            </Typography>
          </Box>
          <IconButton 
            onClick={() => setShowFollowingModal(false)}
            sx={{ color: "#555" }}
            aria-label="close following dialog"
          >
            <CloseIcon />
          </IconButton>
        </Box>
        
        {followingList.length === 0 ? (
          <Box sx={{ 
            textAlign: "center", 
            py: 4,
            color: "#777",
            bgcolor: "#f9f9f9",
            borderRadius: "10px"
          }}>
            <Typography>Not following anyone yet</Typography>
          </Box>
        ) : (
          followingList.map((following) => (
            <Box 
              key={following._id}
              sx={{ 
                display: "flex", 
                alignItems: "center", 
                mb: 2, 
                cursor: "pointer", 
                p: 1.5,
                borderRadius: "12px",
                transition: "all 0.2s ease",
                "&:hover": { 
                  backgroundColor: "#f5f5f5",
                  transform: "translateX(5px)" 
                },
              }}
              onClick={() => {
                setShowFollowingModal(false);
                // Use window.open for new tab navigation
                if (isDesktop) {
                  window.open(`/profile/${following._id}`, '_blank');
                } else {
                  navigate(`/profile/${following._id}`);
                }
              }}
            >
              <UserImage image={following.picturePath} size="50px" />
              <Box sx={{ ml: 2, overflow: "hidden", flex: 1 }}>
                <Typography fontWeight="bold" sx={{ wordBreak: "break-word" }}>
                  {following.firstName} {following.lastName}
                </Typography>
                <Typography color="text.secondary" fontSize="14px" sx={{ wordBreak: "break-word" }}>
                  {following.username}
                </Typography>
              </Box>
              <IconButton 
                size="small" 
                sx={{ color: "#555" }}
                onClick={(e) => {
                  e.stopPropagation();
                  // Handle unfollow action
                }}
              >
                <PersonRemove fontSize="small" />
              </IconButton>
            </Box>
          ))
        )}
      </Box>
    </Modal>

    {/* Follow/Unfollow Status Display */}
    {!isOwnProfile && _id !== userId && (
      <Box 
        sx={{ 
          display: "flex", 
          justifyContent: "center", 
          marginTop: "15px",
          marginBottom: "30px",
        }}
      >
        <Box
          sx={{
            position: "relative",
            padding: "4px",
            borderRadius: "18px",
            background: "linear-gradient(45deg, #FF61D2, #FFA726, #00bfff)",
            width: isMobileScreens ? "85%" : "auto",
            maxWidth: "300px"
          }}
        >
          <Box
            sx={{
              backgroundColor: "#fff",
              borderRadius: "15px",
              padding: "12px 18px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 1.5,
            }}
          >
            {isPendingRequest ? (
              <>
                <Box
                  sx={{
                    bgcolor: "#FFA726",
                    borderRadius: "50%",
                    width: "28px",
                    height: "28px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "white",
                    boxShadow: "0 2px 6px rgba(255, 167, 38, 0.4)"
                  }}
                >
                  <FaUserClock style={{ fontSize: "16px" }} />
                </Box>
                <Typography
                  sx={{
                    fontWeight: 600,
                    background: "linear-gradient(90deg, #FFA726, #FF8F00)",
                    backgroundClip: "text",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    fontSize: isMobileScreens ? "14px" : "16px"
                  }}
                >
                  Follow Request Pending
                </Typography>
              </>
            ) : isFollowing ? (
              <>
                <Box
                  sx={{
                    bgcolor: "#00C853",
                    borderRadius: "50%",
                    width: "28px",
                    height: "28px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "white",
                  }}
                >
                  <CheckCircle style={{ fontSize: "18px" }} />
                </Box>
                <Typography
                  sx={{
                    fontWeight: 600,
                    background: "linear-gradient(90deg, #00C853, #1DE9B6)",
                    backgroundClip: "text",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    fontSize: isMobileScreens ? "14px" : "16px"
                  }}
                >
                  You're Following
                </Typography>
              </>
            ) : (
              <>
                <Box
                  sx={{
                    bgcolor: "#FF61D2",
                    borderRadius: "50%",
                    width: "28px",
                    height: "28px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "white",
                  }}
                >
                  <PersonAdd style={{ fontSize: "16px" }} />
                </Box>
                <Typography
                  sx={{
                    fontWeight: 600,
                    background: "linear-gradient(90deg, #FF61D2, #7C4DFF)",
                    backgroundClip: "text",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    fontSize: isMobileScreens ? "14px" : "16px"
                  }}
                >
                  Not Following Yet
                </Typography>
              </>
            )}
          </Box>
        </Box>
      </Box>
    )}

    {/* Edit Profile Modal */}
    <Modal 
      open={isEditing} 
      onClose={() => setIsEditing(false)}
      aria-labelledby="edit-profile-title"
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{
        timeout: 500,
      }}  
    >
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          bgcolor: "white",
          boxShadow: 24,
          borderRadius: "12px",
          padding: isMobileScreens ? "20px" : "30px",
          width: isMobileScreens ? "90%" : "400px",
          maxHeight: "90vh",
          overflow: "auto",
          textAlign: "center",
        }}
      >
        <Typography variant="h5" fontWeight="bold" mb={2} id="edit-profile-title">
          Edit Profile
        </Typography>

        <TextField
          label="First Name"
          name="firstName"
          fullWidth
          value={formData.firstName}
          onChange={handleInputChange}
          sx={{
            marginBottom: "15px",
            "& .MuiOutlinedInput-root": {
              "&.Mui-focused fieldset": { borderColor: "#FF61D2" },
            },
            "& label.Mui-focused": { color: "#FF61D2" },
          }}
        />

        <TextField
          label="Last Name"
          name="lastName"
          fullWidth
          value={formData.lastName}
          onChange={handleInputChange}
          sx={{ 
            marginBottom: "15px",
            "& .MuiOutlinedInput-root": {
              "&.Mui-focused fieldset": { borderColor: "#FF61D2" },
            },
            "& label.Mui-focused": { color: "#FF61D2" },
          }}
        />

        <TextField
          label="Username"
          name="username"
          fullWidth
          value={formData.username}
          onChange={handleInputChange}
          sx={{ 
            marginBottom: "15px", 
            "& .MuiOutlinedInput-root": {
              "&.Mui-focused fieldset": { borderColor: "#FF61D2" },
            },
            "& label.Mui-focused": { color: "#FF61D2" },
          }}
        />

        <TextField
          label="Bio"
          name="bio"
          fullWidth
          multiline
          rows={3}
          value={formData.bio}
          onChange={handleInputChange}
          sx={{ 
            marginBottom: "20px", 
            "& .MuiOutlinedInput-root": {
              "&.Mui-focused fieldset": { borderColor: "#FF61D2" },
            },
            "& label.Mui-focused": { color: "#FF61D2" },
          }}
        />

        {/* Cancel & Save Buttons */}
        <Box 
          sx={{ 
            display: "flex", 
            justifyContent: "space-between",
            flexDirection: isMobileScreens ? "column" : "row",
            gap: isMobileScreens ? "10px" : 0
          }}
        >
          <Button
            variant="outlined"
            fullWidth={isMobileScreens}
            onClick={() => setIsEditing(false)}
            sx={{
              borderRadius: "8px",
              textTransform: "none",
              padding: "8px 20px",
              fontSize: "16px",
              color: "#FF61D2",
              borderColor: "#FF61D2",
              "&:hover": { 
                backgroundColor: "rgba(255,97,210,0.05)",
                borderColor: "#FF61D2",
                transform: "scale(1.02)",
              },
              transition: "all 0.2s ease"
            }}
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            fullWidth={isMobileScreens}
            onClick={handleSave}
            sx={{
              borderRadius: "8px",
              textTransform: "none",
              backgroundColor: "#000",
              color: "white",
              padding: "8px 20px",
              boxShadow: "none",
              fontSize: "16px",
              "&:hover": { 
                backgroundColor: "#333",
                transform: "scale(1.02)",
              },
              transition: "all 0.2s ease"
            }}
          >
            Save
          </Button>
        </Box>
      </Box>
    </Modal>

  {/* Image Cropping Modal */}
<Modal 
  open={isCropping} 
  onClose={() => setIsCropping(false)}
  aria-labelledby="crop-image-title"
  closeAfterTransition
  BackdropComponent={Backdrop}
  BackdropProps={{
    timeout: 500,
  }}
>
  <Box
    sx={{
      position: "absolute",
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%)",
      width: isMobileScreens ? "90%" : "400px",
      maxWidth: "95vw",
      bgcolor: "white",
      p: 3,
      borderRadius: 2,
      boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
      maxHeight: "90vh",
      overflow: "auto",
    }}
  >
    <Typography fontWeight="bold" mb={2} id="crop-image-title">
      Crop Your Profile Picture
    </Typography>
    
    {/* Cropper Container */}
    <Box 
      sx={{ 
        position: "relative", 
        width: "100%", 
        height: 300, 
        bgcolor: "#f0f0f0", 
        borderRadius: "8px", 
        overflow: "hidden",
        mb: 2
      }}
    >
      {imageSrc && (
        <Cropper
  image={imageSrc}
  crop={crop} // Add this state: const [crop, setCrop] = useState({ x: 0, y: 0 });
  zoom={zoom} // Add this state: const [zoom, setZoom] = useState(1);
  aspect={1}
  onCropChange={setCrop} // Update to use setCrop function
  onCropComplete={(croppedArea, croppedPixels) => setCroppedAreaPixels(croppedPixels)}
  onZoomChange={setZoom} // Update to use setZoom function
  objectFit="contain"
/>

      )}

    </Box>
    {/* Add Zoom Controls RIGHT HERE - Add this code immediately after the above Box */}
<Box sx={{ width: "100%", mt: 2 }}>
  <Typography variant="body2" sx={{ mb: 1, display: "flex", justifyContent: "space-between" }}>
    <span>Zoom</span>
    <span>{Math.round(zoom * 100)}%</span>
  </Typography>
  <input
    type="range"
    value={zoom}
    min={1}
    max={3}
    step={0.1}
    aria-labelledby="Zoom"
    onChange={(e) => setZoom(Number(e.target.value))}
    style={{ 
      width: "100%", 
      accentColor: "#FF61D2" 
    }}
  />
</Box>

    {/* Error Display */}
    {uploadError && (
      <Typography color="error" sx={{ mb: 2, fontSize: "14px" }}>
        {uploadError}
      </Typography>
    )}
    
    {/* Action Buttons */}
    <Box 
      sx={{ 
        display: "flex", 
        justifyContent: "space-between", 
        mt: 2,
        flexDirection: isMobileScreens ? "column" : "row",
        gap: isMobileScreens ? "10px" : "10px"
      }}
    >
      <Button 
        variant="outlined" 
        fullWidth={isMobileScreens}
        onClick={() => {
          setIsCropping(false);
          setImageSrc(null);
          setUploadError(null);
        }}
        disabled={isUploading}
        sx={{
          borderRadius: "8px",
          "&:hover": {
            transform: "scale(1.02)",
          },
          transition: "all 0.2s ease"
        }}
      >
        Cancel
      </Button>
      
      <Button
        variant="contained"
        fullWidth={isMobileScreens}
        disabled={isUploading || !imageSrc}
        onClick={handleCropComplete}
        sx={{
          borderRadius: "8px",
          backgroundColor: "#FF61D2",
          "&:hover": { 
            backgroundColor: "#e046b8",
            transform: "scale(1.02)",
          },
          transition: "all 0.2s ease"
        }}
      >
        {isUploading ? (
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <CircularProgress size={16} sx={{ color: "white", mr: 1 }} />
            Uploading...
          </Box>
        ) : "Save"}
      </Button>
    </Box>
  </Box>
</Modal>

{/* Success Notification */}
<Snackbar
  open={openSnackbar}
  autoHideDuration={3000}
  onClose={() => setOpenSnackbar(false)}
  anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
>
  <Alert 
    severity="success" 
    onClose={() => setOpenSnackbar(false)} 
    sx={{ 
      width: '100%',
      boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
    }}
  >
    Your profile has been successfully updated.
  </Alert>
</Snackbar>
    
    {/* Edit Button for Mobile - Displayed at the bottom of profile */}
    {isOwnProfile && isMobileScreens && (
      <Box sx={{ mt: 3 }}>
        <Button
          variant="contained"
          startIcon={<Edit />}
          onClick={() => setIsEditing(true)}
          fullWidth
          sx={{
            backgroundColor: "#0084FF",
            color: "white",
            borderRadius: "30px",
            textTransform: "none",
            fontSize: "14px",
            padding: "8px 20px",
            boxShadow: "none",
            "&:hover": { backgroundColor: "#0073e6" },
          }}
        >
          Edit Profile
        </Button>
        
      </Box>
      
      
    )}
    {/* User Videos Section - Only shown if following */}
{/* User Videos Section - Only shown if following */}
{/* User Videos Section - Only shown if following */}
{/* User Videos Section - Only shown if following or it's own profile */}
{userId !== _id && !isFollowing ? (
  <Box sx={{ mt: 4, width: "100%" }}>
    <Typography 
      variant="h5" 
      fontWeight="bold" 
      sx={{ 
        mb: 2, 
        textAlign: "left",
        borderBottom: "2px solid #f0f0f0",
        paddingBottom: "10px"
      }}
    >
      <VideoLibrary sx={{ mr: 1, verticalAlign: "middle" }} />
      Videos by {user?.firstName}
    </Typography>
    
    <Box 
      sx={{ 
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        py: 5, 
        bgcolor: "#f9f9f9", 
        borderRadius: "16px",
        color: "#555",
        border: "1px dashed #ddd",
        boxShadow: "inset 0 0 15px rgba(0,0,0,0.05)",
        position: "relative",
        overflow: "hidden"
      }}
    >
      <Box 
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: "6px",
          background: "linear-gradient(90deg, #FF61D2, #FFA726, #00bfff)",
        }}
      />
      
      <Lock sx={{ fontSize: 48, color: "#FF61D2", mb: 2, opacity: 0.8 }} />
      
      <Typography variant="h6" fontWeight="bold" gutterBottom>
        Follow {user?.firstName} to see their videos
      </Typography>
      
      <Typography 
        sx={{ 
          color: "#777", 
          maxWidth: "80%", 
          textAlign: "center",
          mb: 3
        }}
      >
        This content is only visible to followers. Follow {user?.firstName} to unlock their videos and show your support!
      </Typography>
      
      {/* <Button
        variant="contained"
        startIcon={<PersonAdd />}
        onClick={async () => {
          try {
            const result = await sendFollowRequest(_id, userId);
            setIsPendingRequest(result.isPending);
          } catch (error) {
            console.error("Error sending follow request:", error);
          }
        }}
        sx={{
          borderRadius: "30px",
          background: "linear-gradient(45deg, #FF61D2, #FFA726)",
          color: "white",
          textTransform: "none",
          fontWeight: "bold",
          padding: "10px 24px",
          boxShadow: "0 4px 10px rgba(255, 97, 210, 0.3)",
          "&:hover": {
            background: "linear-gradient(45deg, #e046b8, #e0960e)",
            transform: "translateY(-2px)",
            boxShadow: "0 6px 12px rgba(255, 97, 210, 0.4)",
          },
          transition: "all 0.3s ease"
        }}
      >
        Follow {user?.firstName}
      </Button> */}
    </Box>
  </Box>
) : isFollowing && userId !== _id && (
  <Box sx={{ mt: 4, width: "100%" }}>
    <Typography 
      variant="h5" 
      fontWeight="bold" 
      sx={{ 
        mb: 2, 
        textAlign: "left",
        borderBottom: "2px solid #f0f0f0",
        paddingBottom: "10px"
      }}
    >
      <VideoLibrary sx={{ mr: 1, verticalAlign: "middle" }} />
      Videos by {user?.firstName}
    </Typography>
    
    {isLoadingVideos ? (
      <Box sx={{ display: "flex", justifyContent: "center", my: 4 }}>
        <CircularProgress size={40} sx={{ color: "#FF61D2" }} />
      </Box>
    ) : userVideos.length === 0 ? (
      <Box 
        sx={{ 
          textAlign: "center", 
          py: 4, 
          bgcolor: "#f9f9f9", 
          borderRadius: "10px",
          color: "#777" 
        }}
      >
        <VideocamOff sx={{ fontSize: 40, color: "#aaa", mb: 1 }} />
        <Typography>No videos uploaded yet</Typography>
      </Box>
    ) : (
      <Box sx={{ 
        display: "grid",
        gridTemplateColumns: {
          xs: "repeat(1, 1fr)",
          sm: "repeat(2, 1fr)",
          md: "repeat(3, 1fr)",
          lg: "repeat(4, 1fr)"
        },
        gap: 2,
        width: "100%"
      }}>
        {userVideos.map((video) => (
          <Box
            key={video.id}
            data-postid={video.id}
            sx={{
              bgcolor: "white",
              boxShadow: "0 4px 8px rgba(0,0,0,0.12)",
              borderRadius: 2,
              width: "100%",
              p: 1,
              position: "relative",
              border: "1px solid #e0e0e0",
              transition: "transform 0.2s ease, box-shadow 0.2s ease",
              "&:hover": {
                transform: "translateY(-3px)",
                boxShadow: "0 6px 12px rgba(0,0,0,0.15)"
              }
            }}
          >
            {/* Video Section */}
            <Box
              sx={{ 
                position: "relative", 
                display: "flex", 
                flexDirection: "column",
                alignItems: "center",
                width: "100%"
              }}
            >
              <Box
                sx={{
                  width: "100%",
                  height: {
                    xs: "320px",
                    sm: "280px",
                    md: "240px"
                  },
                  borderRadius: 2,
                  overflow: "hidden",
                  background: "#000",
                  position: "relative",
                  border: "3px solid rgba(255, 97, 210, 0.3)",
                  borderImage: "linear-gradient(45deg, #FF61D2, #FFA726, #00bfff) 1",
                  boxShadow: "0 0 15px rgba(255, 97, 210, 0.4)",
                }}
              >
                <video
                  src={video.video_url}
                  muted
                  playsInline
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "contain",
                    cursor: "pointer",
                  }}
                  onClick={() => navigate(`/home/post/${video.id}?source=shared`)}
                />
                
                {/* Play Button */}
                <Box
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    borderRadius: "50%",
                    padding: 0,
                    cursor: "pointer",
                    animation: "pulse 1.8s infinite",
                    "@keyframes pulse": {
                      "0%": { opacity: 0.7, transform: "translate(-50%, -50%) scale(1)" },
                      "50%": { opacity: 1, transform: "translate(-50%, -50%) scale(1.1)" },
                      "100%": { opacity: 0.7, transform: "translate(-50%, -50%) scale(1)" }
                    }
                  }}
                  onClick={() => navigate(`/home/post/${video.id}?source=shared`)}
                >
                  <AiOutlinePlayCircle size={50} color="#fff" />
                </Box>
              </Box>
            </Box>
            
            {/* Only showing hashtags, removed title and timestamp */}
            <Box sx={{ fontSize: 11, color: "#2196f3", mt: 1 }}>
              {video.hashtags?.slice(0, 3).map((tag, i) => (
                <span key={i} style={{ marginRight: 6, cursor: "pointer" }}>
                  {tag}
                </span>
              ))}
              {video.hashtags?.length > 3 && "..."}
            </Box>
          </Box>
        ))}
      </Box>
    )}
  </Box>
)}
 </Box>

  
);};

export default ProfilePage;
